'use client';

import type { PropsWithChildren } from 'react';
import { useMemo, useState } from 'react';
import { merge } from 'lodash';
import type { ApolloClient, NormalizedCacheObject } from '@apollo/client';
import {
  ApolloProvider as DefaultApolloProvider,
  useApolloClient,
} from '@apollo/client';
import { isServer } from '@frameio/utils/config';
import { log } from '@frameio/utils/log';
import { initApolloClientForClientComponent } from './initApolloClientForClientComponent';
import type { ApolloClientOpts } from './types';

/** @private */
let APOLLO_CLIENT: ApolloClient<NormalizedCacheObject>;

export const getApolloClient = () => APOLLO_CLIENT;

function hydrateApolloClientCache(
  client: ApolloClient<NormalizedCacheObject>,
  cache: NormalizedCacheObject
) {
  try {
    const existingCache = client.extract();
    client.cache.restore(merge({}, existingCache, cache));
  } catch (err) {
    log.warn(err);
  }
}

export function initializeApollo(opts: ApolloClientOpts = {}) {
  const client = APOLLO_CLIENT ?? initApolloClientForClientComponent(opts);

  if (opts.initialState) {
    hydrateApolloClientCache(client, opts.initialState);
  }

  if (isServer) {
    return client;
  }

  APOLLO_CLIENT ||= client;

  return client;
}

export function useApollo({
  initialCacheSize,
  initialState,
  isUXPIntegration,
  serverDetails,
  avoidAuthRedirect,
}: ApolloClientOpts | undefined = {}) {
  const store = useMemo(
    () =>
      initializeApollo({
        initialCacheSize,
        initialState,
        isUXPIntegration,
        serverDetails,
        avoidAuthRedirect,
      }),
    [
      initialCacheSize,
      initialState,
      isUXPIntegration,
      serverDetails,
      avoidAuthRedirect,
    ]
  );
  return store;
}

export function useApolloForShares(initialState?: any, shareId?: string) {
  const store = useMemo(
    () =>
      initializeApollo({
        initialState,
        serverDetails: { tokens: { share: shareId } },
      }),
    [initialState, shareId]
  );
  return store;
}

export function useApolloForUploadLinks(
  initialState?: any,
  uploadLinkId?: string
) {
  const store = useMemo(
    () =>
      initializeApollo({
        initialState,
        serverDetails: { tokens: { upload: uploadLinkId } },
      }),
    [initialState, uploadLinkId]
  );
  return store;
}

/**
 *
 * @description Used in tandem with `getApolloCacheForQuery` to
 * fetch data in React Server Components and have the Apollo cache be hydrated
 * with that data on the first client-side client component render.
 */
export function ApolloCacheInitializer({
  cache,
  children,
}: PropsWithChildren<{ cache: NormalizedCacheObject }>) {
  const client = useApolloClient() as ApolloClient<NormalizedCacheObject>;
  const [hasInitialized, setHasInitialized] = useState(false);
  if (!hasInitialized) {
    hydrateApolloClientCache(client, cache);
    setHasInitialized(true);
  }
  return <>{children}</>;
}

export function ApolloProvider({
  children,
  initialCacheSize,
  initialState,
  isUXPIntegration = false,
  serverDetails,
  avoidAuthRedirect,
}: PropsWithChildren<Parameters<typeof useApollo>[0]>) {
  const apolloProviderClient = useApollo({
    initialCacheSize,
    initialState,
    isUXPIntegration,
    serverDetails,
    avoidAuthRedirect,
  });

  return (
    <DefaultApolloProvider client={apolloProviderClient}>
      {children}
    </DefaultApolloProvider>
  );
}
